import { useTranslation } from 'react-i18next';
import { usePublishedPosts } from '@/hooks/useBlog';
import BlogCard from './BlogCard';
import BlogTimelineSidebar from './BlogTimelineSidebar';

interface BlogPanelProps {
  onSlideBack: () => void;
}

export default function BlogPanel({ onSlideBack }: BlogPanelProps) {
  const { t, i18n } = useTranslation('landing');
  const { data: posts, isLoading } = usePublishedPosts(12);

  return (
    <div className="h-full flex bg-[var(--navy-deep)] text-white">
      {/* Timeline sidebar */}
      <div className="w-[72px] flex-shrink-0 border-r border-white/5 relative">
        {/* Back button at top of sidebar */}
        <button
          onClick={onSlideBack}
          className="absolute top-6 left-1/2 -translate-x-1/2 z-20 w-10 h-10 rounded-full bg-white/10 border border-white/10 text-gray-300 hover:text-white hover:bg-white/20 transition-all duration-200 flex items-center justify-center"
          title={t('stories.backToLanding')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {!isLoading && posts && posts.length > 0 && (
          <BlogTimelineSidebar posts={posts} />
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto landing-scrollbar">
        {/* Header */}
        <div className="pt-16 pb-10 px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('stories.title')}</h1>
          <p className="text-lg text-gray-400 max-w-2xl">{t('stories.subtitle')}</p>
        </div>

        {/* Content */}
        <div className="px-8 pb-24">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--cyan-electric)]" />
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  locale={i18n.language}
                  readMoreLabel={t('stories.readMore')}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-white/5 rounded-xl border border-white/10 p-12 max-w-md mx-auto">
                <svg
                  className="w-16 h-16 mx-auto text-gray-600 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                  />
                </svg>
                <p className="text-gray-500">
                  {i18n.language === 'zh-TW'
                    ? '即將推出更多精彩故事！'
                    : 'More stories coming soon!'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
